// src/pages/FichesMissionAdminPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Eye, CheckCircle, XCircle, Clock, Download,
  Filter, Search, Calendar, ChevronDown, ChevronUp,
  AlertCircle, Home, ArrowLeft, User, MapPin,
  Building, Car, Coffee, Hotel, Briefcase
} from 'lucide-react';
import Sidebar from '../components/Sidebar';

const FichesMissionAdminPage = () => {
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [ficheSelectionnee, setFicheSelectionnee] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Filtres
  const [filtres, setFiltres] = useState({
    statut: '',
    search: '',
    dateDebut: '',
    dateFin: ''
  });

  // Nouveaux états pour l'année scolaire et le chiffre d'affaire
  const [anneeScolaireFilter, setAnneeScolaireFilter] = useState('');
  const [anneesDisponibles, setAnneesDisponibles] = useState([]);
  const [chiffreAffaireStats, setChiffreAffaireStats] = useState({
    total: 0,
    parMois: {},
    parMissionUser: {},
    evolution: []
  });

  // Tri
  const [tri, setTri] = useState({ colonne: 'dateCreation', ordre: 'desc' });

  const navigate = useNavigate();

  useEffect(() => {
    fetchAnneesDisponibles();
    fetchFiches();
  }, [filtres, tri, anneeScolaireFilter]);

  const fetchAnneesDisponibles = async () => {
    try {
      const token = localStorage.getItem('token');

      // Récupérer les années scolaires distinctes des fiches
      const response = await axios.get(
        '    https://vmi1977988.contaboserver.net/api2/admin/fiches-mission/annees-scolaires',
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.annees) {
        setAnneesDisponibles(response.data.annees);
        if (response.data.annees.length > 0 && !anneeScolaireFilter) {
          setAnneeScolaireFilter(response.data.annees[0]);
        }
      }
    } catch (err) {
      console.error('Erreur chargement années:', err);
      // Par défaut, utiliser l'année en cours
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;
      const defaultAnnee = `${currentYear}/${nextYear}`;
      setAnneesDisponibles([defaultAnnee]);
      setAnneeScolaireFilter(defaultAnnee);
    }
  };

  const fetchFiches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = new URLSearchParams();
      if (filtres.statut) params.append('statut', filtres.statut);
      if (filtres.search) params.append('search', filtres.search);
      if (filtres.dateDebut) params.append('dateDebut', filtres.dateDebut);
      if (filtres.dateFin) params.append('dateFin', filtres.dateFin);
      if (anneeScolaireFilter) params.append('anneeScolaire', anneeScolaireFilter);
      params.append('sort', `${tri.colonne}:${tri.ordre}`);

      const res = await axios.get(
        `     https://vmi1977988.contaboserver.net/api2/admin/fiches-mission?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFiches(res.data.data || []);
      calculerChiffreAffaire(res.data.data || []);
    } catch (err) {
      console.error('Erreur chargement fiches:', err);
      showMessage('Erreur lors du chargement des fiches', 'error');
      setFiches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  // Fonction pour calculer le chiffre d'affaire des missions
  const calculerChiffreAffaire = (fichesData) => {
    let totalCA = 0;
    const parMois = {};
    const parMissionUser = {};
    const evolution = [];

    fichesData.forEach(fiche => {
      if (fiche.statut === 'Approuvé' || fiche.statut === 'Remboursé') {
        // Calculer le coût total estimé de la mission
        let coutMission = 0;

        // Frais de mission
        if (fiche.fraisMission) {
          if (fiche.fraisMission.repas?.plafond) {
            coutMission += fiche.fraisMission.repas.plafond * (fiche.nombreJours || 0);
          }
          if (fiche.fraisMission.hotel?.plafond) {
            coutMission += fiche.fraisMission.hotel.plafond * (fiche.nombreJours || 0);
          }
          if (fiche.fraisMission.transport?.plafond) {
            coutMission += fiche.fraisMission.transport.plafond;
          }
          if (fiche.fraisMission.autres?.plafond) {
            coutMission += fiche.fraisMission.autres.plafond;
          }
        }

        // Avance sur frais
        if (fiche.avanceSurFrais?.active && fiche.avanceSurFrais.montant) {
          coutMission += fiche.avanceSurFrais.montant;
        }

        totalCA += coutMission;

        // Regroupement par mois
        if (fiche.dateDepart) {
          const date = new Date(fiche.dateDepart);
          const moisKey = date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short' });
          parMois[moisKey] = (parMois[moisKey] || 0) + coutMission;
        }

        // Regroupement par Mission User
        if (fiche.missionUser?.nom) {
          const missionUserName = `${fiche.missionUser.prenom || ''} ${fiche.missionUser.nom || ''}`.trim();
          if (!parMissionUser[missionUserName]) {
            parMissionUser[missionUserName] = {
              nom: missionUserName,
              total: 0,
              missions: 0
            };
          }
          parMissionUser[missionUserName].total += coutMission;
          parMissionUser[missionUserName].missions += 1;
        }

        // Pour l'évolution (trié par date)
        if (fiche.dateDepart) {
          evolution.push({
            date: fiche.dateDepart,
            cout: coutMission,
            mission: fiche.natureMission,
            missionUser: fiche.missionUser?.nom || 'Non spécifié'
          });
        }
      }
    });

    // Trier l'évolution par date
    evolution.sort((a, b) => new Date(a.date) - new Date(b.date));

    setChiffreAffaireStats({
      total: totalCA,
      parMois,
      parMissionUser: Object.values(parMissionUser).sort((a, b) => b.total - a.total),
      evolution
    });
  };

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const handleTri = (colonne) => {
    setTri({
      colonne,
      ordre: tri.colonne === colonne && tri.ordre === 'asc' ? 'desc' : 'asc'
    });
  };

  const openViewModal = async (ficheId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `     https://vmi1977988.contaboserver.net/api2/admin/fiches-mission/${ficheId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFicheSelectionnee(res.data.data);
      setShowViewModal(true);
    } catch (err) {
      console.error('Erreur détaillée:', err.response || err);
      showMessage('Erreur lors du chargement des détails', 'error');
    }
  };

  const openValidationModal = (fiche, decision) => {
    setFicheSelectionnee(fiche);
    setShowValidationModal(decision);
  };

  const handleValidation = async (decision, commentaire = '') => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = decision === 'Approuvé' ? 'approuver' : 'refuser';

      await axios.patch(
        `     https://vmi1977988.contaboserver.net/api2/admin/fiches-mission/${ficheSelectionnee._id}/${endpoint}`,
        { commentaire },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showMessage(`Fiche ${decision.toLowerCase()} avec succès`, 'success');
      setShowValidationModal(false);
      setFicheSelectionnee(null);
      fetchFiches();
    } catch (err) {
      showMessage(err.response?.data?.message || 'Erreur lors de la validation', 'error');
    }
  };

  const generatePDF = (fiche) => {
    const printWindow = window.open('', '', 'width=800,height=600');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Fiche d'Ordre de Mission - ${fiche.natureMission}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            padding: 40px;
            background: white;
            color: #1f2937;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            border-bottom: 3px solid #3b82f6;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .header h1 {
            color: #1f2937;
            font-size: 28px;
            margin-bottom: 10px;
            text-transform: uppercase;
          }
          .header .subtitle {
            color: #6b7280;
            font-size: 14px;
          }
          .statut-badge {
            display: inline-block;
            padding: 8px 20px;
            border-radius: 20px;
            font-weight: 600;
            font-size: 14px;
            margin: 20px 0;
          }
          .statut-approuve { background: #d1fae5; color: #059669; }
          .statut-soumis { background: #dbeafe; color: #3b82f6; }
          .statut-refuse { background: #fee2e2; color: #dc2626; }
          .statut-rembourse { background: #e0e7ff; color: #4f46e5; }
          .section {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          .section-title {
            font-size: 18px;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e5e7eb;
            text-transform: uppercase;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
            margin-bottom: 15px;
          }
          .info-item {
            padding: 12px;
            background: #f9fafb;
            border-radius: 6px;
            border: 1px solid #e5e7eb;
          }
          .info-label {
            font-size: 12px;
            font-weight: 600;
            color: #6b7280;
            margin-bottom: 4px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .info-value {
            font-size: 14px;
            color: #1f2937;
            font-weight: 500;
          }
          .full-width {
            grid-column: 1 / -1;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 12px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            border: 1px solid #e5e7eb;
          }
          th, td {
            padding: 12px;
            text-align: left;
            border: 1px solid #e5e7eb;
          }
          th {
            background: #f9fafb;
            font-weight: 600;
            color: #374151;
            font-size: 13px;
          }
          td {
            font-size: 14px;
            color: #1f2937;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>FICHE D'ORDRE DE MISSION</h1>
          <p class="subtitle">Document officiel de déplacement professionnel</p>
          <div class="statut-badge statut-${fiche.statut.toLowerCase().replace('é', 'e')}">
            ${fiche.statut}
          </div>
        </div>

        <div class="section">
          <div class="section-title">INFORMATIONS DU MISSION USER</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Nom complet</div>
              <div class="info-value">${fiche.nomComplet || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Fonction</div>
              <div class="info-value">${fiche.fonction || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Email</div>
              <div class="info-value">${fiche.email || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Téléphone</div>
              <div class="info-value">${fiche.telephone || 'N/A'}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">OBJET DE LA MISSION</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Nature de la mission</div>
              <div class="info-value">${fiche.natureMission}</div>
            </div>
            ${fiche.autreMission ? `
            <div class="info-item">
              <div class="info-label">Précision</div>
              <div class="info-value">${fiche.autreMission}</div>
            </div>
            ` : ''}
            <div class="info-item full-width">
              <div class="info-label">Description détaillée</div>
              <div class="info-value">${fiche.descriptionMission}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">LIEU ET DUREE DU DEPLACEMENT</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Ville(s) de destination</div>
              <div class="info-value">${fiche.villesDeplacement?.join(', ') || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Durée</div>
              <div class="info-value">${fiche.nombreJours || 0} jours</div>
            </div>
            <div class="info-item">
              <div class="info-label">Date de départ</div>
              <div class="info-value">${new Date(fiche.dateDepart).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Date de retour</div>
              <div class="info-value">${new Date(fiche.dateRetour).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
          </div>
        </div>

        ${fiche.moyensTransport?.length > 0 ? `
        <div class="section">
          <div class="section-title">MOYENS DE TRANSPORT</div>
          <table>
            <thead>
              <tr>
                <th>Moyens de transport</th>
                <th>Kilométrage estimé</th>
                <th>Frais carburant</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${fiche.moyensTransport.join(', ')}</td>
                <td>${fiche.kilometrageEstime || '—'} km</td>
                <td>${fiche.fraisCarburantEstimes || '—'} MAD</td>
              </tr>
            </tbody>
          </table>
        </div>
        ` : ''}

        ${fiche.fraisMission ? `
        <div class="section">
          <div class="section-title">FRAIS DE MISSION AUTORISES</div>
          <table>
            <thead>
              <tr>
                <th>Type de frais</th>
                <th>Plafond autorisé</th>
                <th>Justificatif requis</th>
              </tr>
            </thead>
            <tbody>
              ${fiche.fraisMission.repas?.plafond ? `
              <tr>
                <td>Repas</td>
                <td>${fiche.fraisMission.repas.plafond} MAD/jour</td>
                <td>${fiche.fraisMission.repas.justificatifRequis ? 'Oui' : 'Non'}</td>
              </tr>
              ` : ''}
              ${fiche.fraisMission.hotel?.plafond ? `
              <tr>
                <td>Hébergement</td>
                <td>${fiche.fraisMission.hotel.plafond} MAD/nuit</td>
                <td>${fiche.fraisMission.hotel.justificatifRequis ? 'Oui' : 'Non'}</td>
              </tr>
              ` : ''}
              ${fiche.fraisMission.transport?.plafond ? `
              <tr>
                <td>Transport</td>
                <td>${fiche.fraisMission.transport.plafond} MAD</td>
                <td>${fiche.fraisMission.transport.justificatifRequis ? 'Oui' : 'Non'}</td>
              </tr>
              ` : ''}
              ${fiche.fraisMission.autres?.plafond ? `
              <tr>
                <td>Autres frais ${fiche.fraisMission.autres.description ? `(${fiche.fraisMission.autres.description})` : ''}</td>
                <td>${fiche.fraisMission.autres.plafond} MAD</td>
                <td>${fiche.fraisMission.autres.justificatifRequis ? 'Oui' : 'Non'}</td>
              </tr>
              ` : ''}
            </tbody>
          </table>
        </div>
        ` : ''}

        ${fiche.avanceSurFrais?.active ? `
        <div class="section">
          <div class="section-title">AVANCE SUR FRAIS</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Montant de l'avance demandée</div>
              <div class="info-value">${fiche.avanceSurFrais.montant} MAD</div>
            </div>
          </div>
        </div>
        ` : ''}

        ${fiche.validationHierarchique?.decision ? `
        <div class="section">
          <div class="section-title">VALIDATION HIERARCHIQUE</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Décision</div>
              <div class="info-value">${fiche.validationHierarchique.decision}</div>
            </div>
            ${fiche.validationHierarchique.admin ? `
            <div class="info-item">
              <div class="info-label">Validé par</div>
              <div class="info-value">${fiche.validationHierarchique.admin.nom || 'N/A'}</div>
            </div>
            ` : ''}
            ${fiche.validationHierarchique.dateValidation ? `
            <div class="info-item">
              <div class="info-label">Date de validation</div>
              <div class="info-value">${new Date(fiche.validationHierarchique.dateValidation).toLocaleDateString('fr-FR')}</div>
            </div>
            ` : ''}
            ${fiche.validationHierarchique.commentaire ? `
            <div class="info-item full-width">
              <div class="info-label">Commentaire</div>
              <div class="info-value">${fiche.validationHierarchique.commentaire}</div>
            </div>
            ` : ''}
          </div>
        </div>
        ` : ''}

        <div class="footer">
          <p>Document généré le ${new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })} à ${new Date().toLocaleTimeString('fr-FR')}</p>
          <p style="margin-top: 10px; font-weight: 600;">Fiche d'Ordre de Mission - Confidentiel</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 100);
          }
        </script>
      </body>
      </html>
    `);

    printWindow.document.close();
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'Brouillon': { color: '#f59e0b', bg: '#fef3c7' },
      'Soumis': { color: '#3b82f6', bg: '#dbeafe' },
      'Approuvé': { color: '#059669', bg: '#d1fae5' },
      'Refusé': { color: '#dc2626', bg: '#fee2e2' },
      'Remboursé': { color: '#4f46e5', bg: '#e0e7ff' }
    };
    const badge = badges[statut] || badges['Brouillon'];

    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '600',
        color: badge.color,
        background: badge.bg,
        border: `1px solid ${badge.color}`
      }}>
        {statut}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const calculateTotalFrais = (fiche) => {
    let total = 0;
    if (fiche.fraisMission?.repas?.plafond) {
      total += fiche.fraisMission.repas.plafond * (fiche.nombreJours || 0);
    }
    if (fiche.fraisMission?.hotel?.plafond) {
      total += fiche.fraisMission.hotel.plafond * (fiche.nombreJours || 0);
    }
    if (fiche.fraisMission?.transport?.plafond) {
      total += fiche.fraisMission.transport.plafond;
    }
    if (fiche.fraisMission?.autres?.plafond) {
      total += fiche.fraisMission.autres.plafond;
    }
    return total;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#64748b' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc'
    }}>      <Sidebar onLogout={handleLogout} />

      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 2rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>


            <FileText size={32} style={{ color: '#3b82f6' }} />
            <div>
              <h1 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#1f2937',
                margin: '0'
              }}>
                Fiches d'Ordre de Mission {anneeScolaireFilter ? `- ${anneeScolaireFilter}` : ''}
              </h1>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                Gestion et validation des déplacements professionnels
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Bouton Dashboard uniquement */}

          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto 1.5rem auto',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontWeight: '500',
          background: messageType === 'success' ? '#d1fae5' : '#fee2e2',
          color: messageType === 'success' ? '#065f46' : '#991b1b',
          border: `1px solid ${messageType === 'success' ? '#6ee7b7' : '#fca5a5'}`
        }}>
          {messageType === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message}
        </div>
      )}

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 1rem'
      }}>
        {/* Filtres avec filtre année scolaire */}
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid #e5e7eb',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          {/* Filtre Année Scolaire */}
          <div>
            <label style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              display: 'block',
              marginBottom: '0.5rem'
            }}>
              <Calendar size={16} style={{ display: 'inline', marginRight: '0.25rem' }} />
              Année Scolaire
            </label>
            <select
              value={anneeScolaireFilter}
              onChange={(e) => setAnneeScolaireFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem'
              }}
            >
              <option value="">Toutes les années</option>
              {anneesDisponibles.map((annee) => (
                <option key={annee} value={annee}>{annee}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              display: 'block',
              marginBottom: '0.5rem'
            }}>
              <Search size={16} style={{ display: 'inline', marginRight: '0.25rem' }} />
              Recherche
            </label>
            <input
              type="text"
              value={filtres.search}
              onChange={(e) => setFiltres({ ...filtres, search: e.target.value })}
              placeholder="Mission User, mission, ville..."
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              display: 'block',
              marginBottom: '0.5rem'
            }}>
              <Filter size={16} style={{ display: 'inline', marginRight: '0.25rem' }} />
              Statut
            </label>
            <select
              value={filtres.statut}
              onChange={(e) => setFiltres({ ...filtres, statut: e.target.value })}
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem'
              }}
            >
              <option value="">Tous les statuts</option>
              <option value="Soumis">En attente</option>
              <option value="Approuvé">Approuvées</option>
              <option value="Refusé">Refusées</option>
              <option value="Remboursé">Remboursées</option>
            </select>
          </div>

          <div>
            <label style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              display: 'block',
              marginBottom: '0.5rem'
            }}>
              <Calendar size={16} style={{ display: 'inline', marginRight: '0.25rem' }} />
              Date début
            </label>
            <input
              type="date"
              value={filtres.dateDebut}
              onChange={(e) => setFiltres({ ...filtres, dateDebut: e.target.value })}
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              display: 'block',
              marginBottom: '0.5rem'
            }}>
              <Calendar size={16} style={{ display: 'inline', marginRight: '0.25rem' }} />
              Date fin
            </label>
            <input
              type="date"
              value={filtres.dateFin}
              onChange={(e) => setFiltres({ ...filtres, dateFin: e.target.value })}
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem'
              }}
            />
          </div>
        </div>

        {/* Tableau Excel-like */}
        <div style={{
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            padding: '1rem',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {fiches.length} fiches trouvées
            </div>
            <button
              onClick={fetchFiches}
              style={{
                background: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.375rem 0.75rem',
                fontSize: '0.75rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              <Clock size={14} />
              Actualiser
            </button>
          </div>

          {fiches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
              <FileText size={48} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
              <h3 style={{ color: '#374151', marginBottom: '0.5rem' }}>
                Aucune fiche trouvée
              </h3>
              <p style={{ color: '#6b7280' }}>
                Aucune fiche ne correspond à vos critères de recherche
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: '1000px'
              }}>
                <thead>
                  <tr style={{
                    background: '#f9fafb',
                    borderBottom: '2px solid #e5e7eb'
                  }}>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '0.75rem',
                      border: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }} onClick={() => handleTri('missionUser.nom')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        Mission User
                        {tri.colonne === 'missionUser.nom' && (
                          tri.ordre === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                        )}
                      </div>
                    </th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '0.75rem',
                      border: '1px solid #e5e7eb',
                      whiteSpace: 'nowrap'
                    }}>
                      Mission
                    </th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '0.75rem',
                      border: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }} onClick={() => handleTri('dateDepart')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        Date départ
                        {tri.colonne === 'dateDepart' && (
                          tri.ordre === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                        )}
                      </div>
                    </th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '0.75rem',
                      border: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }} onClick={() => handleTri('statut')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        Statut
                        {tri.colonne === 'statut' && (
                          tri.ordre === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                        )}
                      </div>
                    </th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '0.75rem',
                      border: '1px solid #e5e7eb',
                      whiteSpace: 'nowrap'
                    }}>
                      Coût estimé
                    </th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '0.75rem',
                      border: '1px solid #e5e7eb',
                      whiteSpace: 'nowrap'
                    }}>
                      Montant demandé
                    </th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '0.75rem',
                      border: '1px solid #e5e7eb',
                      whiteSpace: 'nowrap'
                    }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fiches.map((fiche, index) => (
                    <tr key={fiche._id} style={{
                      borderBottom: '1px solid #e5e7eb',
                      background: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                      transition: 'background-color 0.2s'
                    }}>
                      <td style={{
                        padding: '0.75rem',
                        fontSize: '0.75rem',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: '600', color: '#1f2937' }}>
                            {fiche.nomComplet || 'N/A'}
                          </span>
                          <span style={{ fontSize: '0.625rem', color: '#6b7280' }}>
                            {fiche.email || ''}
                          </span>
                        </div>
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        fontSize: '0.75rem',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: '600', color: '#1f2937' }}>
                            {fiche.natureMission}
                          </span>
                          <span style={{ fontSize: '0.625rem', color: '#6b7280' }}>
                            {fiche.villesDeplacement?.join(', ') || ''}
                          </span>
                        </div>
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        fontSize: '0.75rem',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ color: '#1f2937' }}>
                            {formatDate(fiche.dateDepart)}
                          </span>
                          <span style={{ fontSize: '0.625rem', color: '#6b7280' }}>
                            {fiche.nombreJours} jours
                          </span>
                        </div>
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        border: '1px solid #e5e7eb'
                      }}>
                        {getStatutBadge(fiche.statut)}
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#059669',
                        border: '1px solid #e5e7eb'
                      }}>
                        {calculateTotalFrais(fiche) > 0 ? (
                          <span>{formatMoney(calculateTotalFrais(fiche))} MAD</span>
                        ) : (
                          <span style={{ color: '#6b7280' }}>—</span>
                        )}
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#f59e0b',
                        border: '1px solid #e5e7eb'
                      }}>
                        {fiche.avanceSurFrais?.active && fiche.avanceSurFrais.montant ? (
                          <span>{formatMoney(fiche.avanceSurFrais.montant)} MAD</span>
                        ) : (
                          <span style={{ color: '#6b7280' }}>—</span>
                        )}
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button
                            onClick={() => openViewModal(fiche._id)}
                            style={{
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '2px',
                              padding: '0.25rem 0.5rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.125rem',
                              fontSize: '0.625rem'
                            }}
                          >
                            <Eye size={12} />
                            Voir
                          </button>
                          {fiche.statut === 'Soumis' && (
                            <>
                              <button
                                onClick={() => openValidationModal(fiche, 'Approuvé')}
                                style={{
                                  background: '#059669',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '2px',
                                  padding: '0.25rem 0.5rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.125rem',
                                  fontSize: '0.625rem'
                                }}
                              >
                                <CheckCircle size={12} />
                                Accepter
                              </button>
                              <button
                                onClick={() => openValidationModal(fiche, 'Refusé')}
                                style={{
                                  background: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '2px',
                                  padding: '0.25rem 0.5rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.125rem',
                                  fontSize: '0.625rem'
                                }}
                              >
                                <XCircle size={12} />
                                Refuser
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => generatePDF(fiche)}
                            style={{
                              background: 'transparent',
                              border: '1px solid #3b82f6',
                              borderRadius: '2px',
                              padding: '0.25rem 0.5rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.125rem',
                              fontSize: '0.625rem',
                              color: '#3b82f6'
                            }}
                          >
                            <Download size={12} />
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Validation */}
      {showValidationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '400px',
            padding: '1.5rem'
          }}>
            <h3 style={{
              margin: '0 0 1rem 0',
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {showValidationModal === 'Approuvé' ? (
                <>
                  <CheckCircle size={20} style={{ color: '#059669' }} />
                  Approuver la fiche
                </>
              ) : (
                <>
                  <XCircle size={20} style={{ color: '#dc2626' }} />
                  Refuser la fiche
                </>
              )}
            </h3>

            <p style={{ color: '#6b7280', marginBottom: '1rem', fontSize: '0.875rem' }}>
              {showValidationModal === 'Approuvé'
                ? 'Confirmez-vous l\'approbation de cette fiche de mission ?'
                : 'Veuillez saisir un commentaire pour justifier le refus :'}
            </p>

            {showValidationModal === 'Refusé' && (
              <textarea
                id="commentaire"
                rows="3"
                placeholder="Raison du refus..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit'
                }}
              />
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setShowValidationModal(false)}
                style={{
                  flex: 1,
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  const commentaire = showValidationModal === 'Refusé'
                    ? document.getElementById('commentaire')?.value || ''
                    : '';
                  handleValidation(showValidationModal, commentaire);
                }}
                style={{
                  flex: 1,
                  background: showValidationModal === 'Approuvé' ? '#059669' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualisation */}
      {showViewModal && ficheSelectionnee && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem',
          overflow: 'auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Header de la modal */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: 'white',
              zIndex: 10
            }}>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <FileText size={20} />
                  Détails de la fiche de mission
                </h3>
                <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
                  Référence: {ficheSelectionnee.reference || 'N/A'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => generatePDF(ficheSelectionnee)}
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    fontSize: '0.875rem'
                  }}
                >
                  <Download size={16} />
                  Télécharger PDF
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setFicheSelectionnee(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    padding: '0.5rem',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Contenu de la modal */}
            <div style={{ padding: '1.5rem' }}>
              {/* Section Statut et Dates */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <div>
                  {getStatutBadge(ficheSelectionnee.statut)}
                  {ficheSelectionnee.validationHierarchique?.dateValidation && (
                    <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      Validé le: {formatDate(ficheSelectionnee.validationHierarchique.dateValidation)}
                    </p>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '0.875rem', color: '#374151', margin: 0 }}>
                    <strong>Date de création:</strong> {formatDate(ficheSelectionnee.dateCreation || ficheSelectionnee.createdAt)}
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#374151', margin: '0.25rem 0 0 0' }}>
                    <strong>Départ:</strong> {formatDate(ficheSelectionnee.dateDepart)}
                    {' → '}
                    <strong>Retour:</strong> {formatDate(ficheSelectionnee.dateRetour)}
                  </p>
                </div>
              </div>

              {/* Informations Mission User */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#374151',
                  margin: '0 0 0.75rem 0',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <User size={16} />
                  Informations du Mission User
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem'
                }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Nom complet</p>
                    <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                      {ficheSelectionnee.nomComplet || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Fonction</p>
                    <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                      {ficheSelectionnee.fonction || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Email</p>
                    <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                      {ficheSelectionnee.email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Téléphone</p>
                    <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                      {ficheSelectionnee.telephone || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Détails Mission */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#374151',
                  margin: '0 0 0.75rem 0',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Briefcase size={16} />
                  Détails de la Mission
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Nature de la mission</p>
                    <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                      {ficheSelectionnee.natureMission}
                    </p>
                  </div>
                  {ficheSelectionnee.autreMission && (
                    <div>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Autre précision</p>
                      <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                        {ficheSelectionnee.autreMission}
                      </p>
                    </div>
                  )}
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Durée</p>
                    <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                      {ficheSelectionnee.nombreJours || 0} jours
                    </p>
                  </div>
                </div>

                <div>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Description</p>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#1f2937',
                    margin: '0.25rem 0',
                    background: '#f9fafb',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    minHeight: '60px'
                  }}>
                    {ficheSelectionnee.descriptionMission || 'Aucune description'}
                  </div>
                </div>
              </div>

              {/* Villes de déplacement */}
              {ficheSelectionnee.villesDeplacement?.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 0.75rem 0',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <MapPin size={16} />
                    Villes de Déplacement
                  </h4>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    {ficheSelectionnee.villesDeplacement.map((ville, index) => (
                      <span key={index} style={{
                        background: '#dbeafe',
                        color: '#1e40af',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        border: '1px solid #93c5fd'
                      }}>
                        {ville}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Moyens de transport */}
              {ficheSelectionnee.moyensTransport?.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 0.75rem 0',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Car size={16} />
                    Moyens de Transport
                  </h4>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    {ficheSelectionnee.moyensTransport.map((transport, index) => (
                      <span key={index} style={{
                        background: '#f0f9ff',
                        color: '#0369a1',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        border: '1px solid #bae6fd'
                      }}>
                        {transport}
                      </span>
                    ))}
                  </div>
                  {(ficheSelectionnee.kilometrageEstime || ficheSelectionnee.fraisCarburantEstimes) && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '1rem',
                      marginTop: '0.75rem'
                    }}>
                      {ficheSelectionnee.kilometrageEstime && (
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Kilométrage estimé</p>
                          <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                            {ficheSelectionnee.kilometrageEstime} km
                          </p>
                        </div>
                      )}
                      {ficheSelectionnee.fraisCarburantEstimes && (
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Frais carburant</p>
                          <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                            {formatMoney(ficheSelectionnee.fraisCarburantEstimes)} MAD
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Frais de mission */}
              {ficheSelectionnee.fraisMission && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 0.75rem 0',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    Frais de Mission Autorisés
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '0.75rem'
                  }}>
                    {ficheSelectionnee.fraisMission.repas?.plafond && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <Coffee size={14} style={{ color: '#059669' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Repas</span>
                        </div>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Plafond: {formatMoney(ficheSelectionnee.fraisMission.repas.plafond)} MAD/jour
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Total estimé: {formatMoney(ficheSelectionnee.fraisMission.repas.plafond * (ficheSelectionnee.nombreJours || 0))} MAD
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Justificatif: {ficheSelectionnee.fraisMission.repas.justificatifRequis ? 'Requis' : 'Non requis'}
                        </p>
                      </div>
                    )}

                    {ficheSelectionnee.fraisMission.hotel?.plafond && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <Hotel size={14} style={{ color: '#3b82f6' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Hébergement</span>
                        </div>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Plafond: {formatMoney(ficheSelectionnee.fraisMission.hotel.plafond)} MAD/nuit
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Total estimé: {formatMoney(ficheSelectionnee.fraisMission.hotel.plafond * (ficheSelectionnee.nombreJours || 0))} MAD
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Justificatif: {ficheSelectionnee.fraisMission.hotel.justificatifRequis ? 'Requis' : 'Non requis'}
                        </p>
                      </div>
                    )}

                    {ficheSelectionnee.fraisMission.transport?.plafond && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <Car size={14} style={{ color: '#f59e0b' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Transport</span>
                        </div>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Plafond: {formatMoney(ficheSelectionnee.fraisMission.transport.plafond)} MAD
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Justificatif: {ficheSelectionnee.fraisMission.transport.justificatifRequis ? 'Requis' : 'Non requis'}
                        </p>
                      </div>
                    )}

                    {ficheSelectionnee.fraisMission.autres?.plafond && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <Building size={14} style={{ color: '#8b5cf6' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                            Autres frais {ficheSelectionnee.fraisMission.autres.description ? `(${ficheSelectionnee.fraisMission.autres.description})` : ''}
                          </span>
                        </div>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Plafond: {formatMoney(ficheSelectionnee.fraisMission.autres.plafond)} MAD
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.125rem 0' }}>
                          Justificatif: {ficheSelectionnee.fraisMission.autres.justificatifRequis ? 'Requis' : 'Non requis'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Total estimé */}
                  <div style={{
                    background: '#f0f9ff',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #7dd3fc',
                    marginTop: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#0369a1' }}>
                      Total estimé des frais
                    </span>
                    <span style={{ fontSize: '1rem', fontWeight: '700', color: '#059669' }}>
                      {formatMoney(calculateTotalFrais(ficheSelectionnee))} MAD
                    </span>
                  </div>
                </div>
              )}

              {/* Avance sur frais */}
              {ficheSelectionnee.avanceSurFrais?.active && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 0.75rem 0',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    Avance sur Frais Demandée
                  </h4>
                  <div style={{
                    background: '#fef3c7',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #fbbf24'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontSize: '0.75rem', color: '#92400e', margin: 0 }}>Montant demandé</p>
                        <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#92400e', margin: '0.25rem 0 0 0' }}>
                          {formatMoney(ficheSelectionnee.avanceSurFrais.montant)} MAD
                        </p>
                      </div>
                      {ficheSelectionnee.avanceSurFrais.raison && (
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#92400e', margin: 0 }}>Raison</p>
                          <p style={{ fontSize: '0.875rem', color: '#92400e', margin: '0.25rem 0 0 0' }}>
                            {ficheSelectionnee.avanceSurFrais.raison}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Hiérarchique */}
              {ficheSelectionnee.validationHierarchique?.decision && (
                <div style={{
                  background: '#f9fafb',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  marginTop: '1.5rem'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 0.75rem 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {ficheSelectionnee.validationHierarchique.decision === 'Approuvé' ? (
                      <>
                        <CheckCircle size={16} style={{ color: '#059669' }} />
                        Fiche approuvée
                      </>
                    ) : (
                      <>
                        <XCircle size={16} style={{ color: '#dc2626' }} />
                        Fiche refusée
                      </>
                    )}
                  </h4>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '0.75rem'
                  }}>
                    {ficheSelectionnee.validationHierarchique.admin && (
                      <div>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Validé par</p>
                        <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                          {ficheSelectionnee.validationHierarchique.admin.nom || 'N/A'}
                        </p>
                      </div>
                    )}
                    {ficheSelectionnee.validationHierarchique.dateValidation && (
                      <div>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Date de validation</p>
                        <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0.25rem 0' }}>
                          {formatDate(ficheSelectionnee.validationHierarchique.dateValidation)}
                        </p>
                      </div>
                    )}
                  </div>

                  {ficheSelectionnee.validationHierarchique.commentaire && (
                    <div>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Commentaire</p>
                      <p style={{
                        fontSize: '0.875rem',
                        color: '#1f2937',
                        margin: '0.25rem 0',
                        fontStyle: 'italic',
                        background: 'white',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}>
                        "${ficheSelectionnee.validationHierarchique.commentaire}"
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default FichesMissionAdminPage;